// DynamicDropdown.jsx
/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Select, { components } from 'react-select';
import { Form, Col } from 'react-bootstrap';
import { FormLabel } from './FormLabel';
import ErrorFeedback from './ErrorFeedback';
import { getValue } from 'helpers/utils';

const CustomValueContainer = ({ children, ...props }) => {
  const value = props.getValue();
  const isMulti = props.selectProps.isMulti;
  return (
    <components.ValueContainer {...props}>
      {isMulti && value.length > 2 ? (
        <span>{`${value.length} options selected`}</span>
      ) : (
        children
      )}
    </components.ValueContainer>
  );
};

const DynamicDropdown = ({
  metadata,
  control,
  errors,
  isDisabled,
  istextAndUnit,
  fetchtextAndUnit,
  formMetadata,
  onChange // External onChange prop
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectRef = useRef(null);
  const { setValue, getValues, watch } = useFormContext();

  const watchedDependencies = metadata.dependsOn
    ? Array.isArray(metadata.dependsOn)
      ? metadata.dependsOn
      : [metadata.dependsOn]
    : [];

  const dependencyValues = watch(watchedDependencies);
  const currentFieldValue = watch(metadata.id);

  const onChangeTimeoutRef = useRef(null);
  const lastDependencyHash = useRef('');
  const isSettingValue = useRef(false);
  const optionsLoadingRef = useRef(false);

  useEffect(() => {
    if (metadata.dependsOn && !dependenciesSatisfied) {
      optionsLoadingRef.current = true;
    } else if (!metadata.dependsOn) {
      optionsLoadingRef.current = false;
    }
  }, []);

  const checkDependencyValues = React.useCallback(() => {
    if (!metadata.dependsOn) return true;
    if (Array.isArray(metadata.dependsOn)) {
      return metadata.dependsOn.every((depField, index) => {
        const value = dependencyValues[index];
        return value && value !== '' && value !== 'null';
      });
    } else {
      const value = dependencyValues[0];
      return value && value !== '' && value !== 'null';
    }
  }, [metadata.dependsOn, dependencyValues]);

  const dependenciesSatisfied = checkDependencyValues();

  const currentDependencyHash = React.useMemo(() => {
    return JSON.stringify(dependencyValues);
  }, [dependencyValues]);

  const dependenciesChanged = React.useMemo(() => {
    const changed = lastDependencyHash.current !== currentDependencyHash;
    if (changed && metadata.dependsOn) {
      optionsLoadingRef.current = true;
      console.log(
        `🔄 Dependencies changed for ${metadata.id}, setting loading state`
      );
    }
    lastDependencyHash.current = currentDependencyHash;
    return changed;
  }, [currentDependencyHash, metadata.dependsOn, metadata.id]);

  const excludedValues = React.useMemo(() => {
    if (
      optionsLoadingRef.current ||
      !metadata.options ||
      metadata.options.length === 0
    ) {
      return [];
    }
    const formState = getValues();
    const fieldsToExcludeFrom = Object.values(
      formMetadata?.fields || {}
    ).filter(
      f =>
        f.id !== metadata.id &&
        f.exclude_selected_from &&
        metadata.exclude_selected_from
    );
    let excluded = [];
    fieldsToExcludeFrom.forEach(f => {
      const selected = formState[f.id];
      if (Array.isArray(selected)) {
        excluded.push(...selected);
      } else if (selected) {
        excluded.push(selected);
      }
    });
    return excluded;
  }, [
    getValues,
    formMetadata?.fields,
    metadata.id,
    metadata.exclude_selected_from,
    metadata.options
  ]);

  const filteredOptions = React.useMemo(() => {
    const options = metadata.options || [];
    return options.filter(opt => !excludedValues.includes(opt.value));
  }, [metadata.options, excludedValues]);

  useEffect(() => {
    if (metadata.options && metadata.options.length > 0) {
      optionsLoadingRef.current = false;
      console.log(
        `✅ Options loaded for ${metadata.id}, clearing loading state`
      );
    }
  }, [metadata.options, metadata.id]);

  const formatGroupedOptions = React.useCallback(options => {
    if (!options) return [];
    if (options[0]?.sectionLabel) {
      return options.map(group => ({
        label: group.sectionLabel,
        options: group.options.map(option => ({
          value: option.value,
          label: option.label
        }))
      }));
    }
    return options.map(option => ({
      value: option.value,
      label: option.label
    }));
  }, []);

  const selectOptions = React.useMemo(
    () => formatGroupedOptions(filteredOptions),
    [formatGroupedOptions, filteredOptions]
  );

  const flattenOptions = React.useCallback(options => {
    return options.reduce((acc, group) => {
      if (group.options) return [...acc, ...group.options];
      return [...acc, group];
    }, []);
  }, []);

  const flatOptions = React.useMemo(
    () => flattenOptions(selectOptions),
    [flattenOptions, selectOptions]
  );

  const formatValue = React.useCallback(
    (options, value) => {
      if (!value) return metadata.multiSelect ? [] : null;
      if (optionsLoadingRef.current && !options.length) {
        return metadata.multiSelect ? [] : null;
      }
      const findOption = val => flatOptions.find(opt => opt.value === val);
      try {
        let returnValue = metadata.multiSelect
          ? (Array.isArray(value) ? value : [value])
              .map(findOption)
              .filter(Boolean)
          : findOption(value);
        if (
          typeof fetchtextAndUnit === 'function' &&
          !metadata.multiSelect &&
          value
        ) {
          const foundOption = findOption(value);
          if (foundOption) fetchtextAndUnit(foundOption);
        }
        return returnValue;
      } catch (error) {
        console.error('Error formatting value:', error);
        return metadata.multiSelect ? [] : null;
      }
    },
    [metadata.multiSelect, flatOptions, fetchtextAndUnit]
  );

  const handleOnChange = React.useCallback(
    newValue => {
      if (isSettingValue.current) {
        console.log(
          `⏸️ Skipping onChange for ${metadata.id} - already setting value`
        );
        return;
      }
      if (onChangeTimeoutRef.current) clearTimeout(onChangeTimeoutRef.current);
      onChangeTimeoutRef.current = setTimeout(() => {
        if (onChange && typeof onChange === 'function') onChange(newValue);
      }, 100);
    },
    [onChange, metadata.id]
  );

  const handleSectionClick = React.useCallback(
    (group, value, onChangeHandler) => {
      if (!metadata.multiSelect) return;
      const groupValues = group.options.map(option => option.value);
      const isGroupSelected = groupValues.every(val =>
        Array.isArray(value) ? value.includes(val) : false
      );
      const newValue = isGroupSelected
        ? value.filter(val => !groupValues.includes(val))
        : [...(Array.isArray(value) ? value : []), ...groupValues];
      onChangeHandler(newValue);
    },
    [metadata.multiSelect]
  );

  const handleSelectAllClick = React.useCallback(
    (value, onChangeHandler) => {
      if (!metadata.multiSelect) return;
      const allValues = flatOptions.map(option => option.value);
      const isAllSelected = allValues.every(val =>
        Array.isArray(value) ? value.includes(val) : false
      );
      const newValue = isAllSelected ? [] : allValues;
      onChangeHandler(newValue);
    },
    [metadata.multiSelect, flatOptions]
  );

  const handleClickOutside = React.useCallback(event => {
    if (selectRef.current && !selectRef.current.contains(event.target))
      setMenuIsOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (
      !metadata.multiSelect &&
      flatOptions.length === 1 &&
      !isSettingValue.current &&
      !currentFieldValue
    ) {
      const newValue = flatOptions[0].value;
      isSettingValue.current = true;
      setValue(metadata.id, newValue);
      handleOnChange(newValue);
      setTimeout(() => (isSettingValue.current = false), 100);
    }
  }, [
    metadata.multiSelect,
    flatOptions.length,
    metadata.id,
    currentFieldValue,
    setValue,
    handleOnChange
  ]);

  useEffect(() => {
    if (metadata.dependsOn && dependenciesChanged) {
      if (!dependenciesSatisfied) {
        const currentValue = currentFieldValue;
        if (
          currentValue !== null &&
          currentValue !== '' &&
          !(Array.isArray(currentValue) && currentValue.length === 0)
        ) {
          console.log(
            `🧹 Clearing ${metadata.id} - dependencies not satisfied`
          );
          isSettingValue.current = true;
          setValue(metadata.id, null);
          handleOnChange(null);
          setTimeout(() => (isSettingValue.current = false), 100);
        }
      } else {
        optionsLoadingRef.current = true;
      }
    }
  }, [
    metadata.id,
    metadata.dependsOn,
    dependenciesSatisfied,
    dependenciesChanged,
    currentFieldValue,
    setValue,
    handleOnChange
  ]);

  useEffect(
    () => () => {
      if (onChangeTimeoutRef.current) clearTimeout(onChangeTimeoutRef.current);
    },
    []
  );

  const customStyles = React.useMemo(
    () => ({
      control: provided => ({
        ...provided,
        minHeight: '80px',
        backgroundColor: 'white',
        ':focus-within': {
          boxShadow:
            'inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 5px var(--falcon-soft-primary) !important;'
        }
      }),
      valueContainer: provided => ({
        ...provided,
        whiteSpace: 'nowrap',
        overflowY: 'auto',
        textOverflow: 'ellipsis',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignContent: 'flex-start',
        maxHeight: '90px'
      }),
      multiValue: provided => ({ ...provided, height: 'fit-content' })
    }),
    []
  );

  const fieldIsDisabled =
    !metadata.isEditable ||
    (metadata.dependsOn && !dependenciesSatisfied) ||
    isDisabled;

  return (
    <Col>
      <Form.Group
        as={Col}
        sm={12}
        md={12}
        style={{
          width: istextAndUnit ? '118px' : '',
          marginTop: istextAndUnit ? '7px' : '',
          borderTopLeftRadius: 'inherit'
        }}
        className="mb-3"
      >
        <FormLabel
          label={istextAndUnit ? '' : metadata.label}
          required={istextAndUnit ? '' : metadata?.validation?.required}
          info_key={metadata?.info_key}
        />
        <div ref={selectRef}>
          <Controller
            name={istextAndUnit ? `${metadata.id}1` : metadata.id}
            control={control}
            rules={{
              required: istextAndUnit ? false : metadata.validation?.required
            }}
            render={({
              field: { value, onChange: fieldOnChange, ...field }
            }) => (
              <Select
                {...field}
                isMulti={metadata.multiSelect}
                isDisabled={fieldIsDisabled}
                isClearable={istextAndUnit ? false : true}
                styles={customStyles}
                options={[
                  ...(metadata.multiSelect
                    ? [
                        {
                          label: 'Select All',
                          value: 'select-all',
                          isSelectAll: true
                        }
                      ]
                    : []),
                  ...selectOptions
                ]}
                value={formatValue(selectOptions, value)}
                onChange={(selectedOptions, actionMeta) => {
                  if (isSettingValue.current) {
                    console.log(
                      `⏸️ Ignoring Select onChange for ${metadata.id} - value being set programmatically`
                    );
                    return;
                  }
                  let newValue;
                  if (metadata.multiSelect) {
                    if (!selectedOptions) {
                      newValue = null;
                      fieldOnChange(newValue);
                      handleOnChange(newValue);
                      return;
                    }
                    const selectedValues = Array.isArray(selectedOptions)
                      ? selectedOptions.map(option => option.value)
                      : [];
                    if (selectedOptions.some(opt => opt.isSelectAll)) {
                      handleSelectAllClick(value, selectAllValue => {
                        fieldOnChange(selectAllValue);
                        handleOnChange(selectAllValue);
                      });
                    } else {
                      newValue =
                        selectedValues.length === 0 ? null : selectedValues;
                      fieldOnChange(newValue);
                      handleOnChange(newValue);
                    }
                  } else {
                    newValue = selectedOptions ? selectedOptions.value : null;
                    fieldOnChange(newValue);
                    handleOnChange(newValue);
                    setMenuIsOpen(false);
                  }
                }}
                classNamePrefix="react-select"
                className={errors[metadata.id] ? 'is-invalid' : ''}
                components={{
                  GroupHeading: props => (
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '0.9em',
                        color: '#666',
                        cursor: 'pointer'
                      }}
                      onClick={() =>
                        handleSectionClick(props.data, value, sectionValue => {
                          fieldOnChange(sectionValue);
                          handleOnChange(sectionValue);
                        })
                      }
                    >
                      {props.children}
                    </div>
                  ),
                  ValueContainer: CustomValueContainer
                }}
                menuIsOpen={menuIsOpen}
                onMenuOpen={() => setMenuIsOpen(true)}
                onMenuClose={() => setMenuIsOpen(false)}
                closeMenuOnSelect={!metadata.multiSelect}
                hideSelectedOptions={false}
                // isLoading={optionsLoadingRef.current}
                // loadingMessage={() => 'Loading options...'}
              />
            )}
          />
          {metadata?.show_count_message && (
            <>
              {(metadata?.dependents || []).map(depId => {
                const targetField = formMetadata?.fields?.[depId];
                const options = targetField?.options ?? [];
                return (
                  <div key={depId} className="text-muted small">
                    {Array.isArray(options) && options.length > 0
                      ? `${options.length} ${
                          options.length === 1 ? 'entry' : 'entries'
                        } ready`
                      : `No data found. Try a different ${metadata.label}`}
                  </div>
                );
              })}
            </>
          )}
        </div>
        <ErrorFeedback
          key={metadata.id}
          error={errors[metadata.id]}
          istextAndUnit={istextAndUnit}
        />
      </Form.Group>
    </Col>
  );
};

export default DynamicDropdown;
